import { useCallback, useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';

// Stripe publishable key  -  surfaced via Vite env at build time. Falls back to
// the test-mode key so dev environments work without ceremony.
const STRIPE_PK = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '';
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const PLANS = [
  { id: 'indie',    name: 'Indie',    price: 29,   included: '1,000',   overage: '$0.005 / record' },
  { id: 'team',     name: 'Team',     price: 199,  included: '10,000',  overage: '$0.003 / record', recommended: true },
  { id: 'business', name: 'Business', price: 999,  included: '100,000', overage: '$0.001 / record' },
];

// PlanPicker  -  three-tier selector. Click "Subscribe" -> POST /v1/checkout
// returning a client_secret -> render <EmbeddedCheckout/> in a modal so the
// customer never leaves the dashboard. Same UX as Claude.ai.
export default function PlanPicker({ workspaceId }) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);

  async function subscribe(plan) {
    setErr(null);
    setBusy(plan);
    try {
      const res = await fetch('/v1/checkout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ plan, workspace_id: workspaceId, ui_mode: 'embedded' }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.client_secret) {
        const msg = data?.error?.message || `HTTP ${res.status}`;
        setErr(msg);
        return;
      }
      setClientSecret(data.client_secret);
    } catch (ex) {
      setErr(ex.message || 'Network error');
    } finally {
      setBusy(null);
    }
  }

  function close() {
    setClientSecret(null);
  }

  return (
    <>
      <div style={grid}>
        {PLANS.map((p) => (
          <div key={p.id} style={{ ...card, ...(p.recommended ? cardRecommended : {}) }}>
            {p.recommended && <div style={badge}>Recommended</div>}
            <div style={planName}>{p.name}</div>
            <div style={planPrice}>${p.price}<span style={{ fontSize: 14, color: 'var(--fg-3)' }}> / mo</span></div>
            <ul style={featureList}>
              <li>{p.included} record-deliveries / mo included</li>
              <li>{p.overage} over the included quota</li>
              <li>Cancel anytime</li>
            </ul>
            <button
              onClick={() => subscribe(p.id)}
              disabled={busy != null}
              style={{ ...subscribeBtn, ...(p.recommended ? subscribeBtnRec : {}) }}
            >
              {busy === p.id ? <Loader2 size={14} className="spin" /> : 'Subscribe'}
            </button>
          </div>
        ))}
      </div>
      {err && <div role="alert" style={errBox}>{err}</div>}
      <p style={{ fontSize: 12, color: 'var(--fg-3)', textAlign: 'center', margin: 0, marginTop: 12 }}>
        Cancel anytime from the customer portal.
      </p>

      {clientSecret && stripePromise && (
        <CheckoutModal clientSecret={clientSecret} onClose={close} />
      )}
      {clientSecret && !stripePromise && (
        <div style={errBox}>VITE_STRIPE_PUBLISHABLE_KEY missing in build  -  embedded checkout unavailable.</div>
      )}
    </>
  );
}

// CheckoutModal mounts EmbeddedCheckout. The session is single-use; closing
// the modal discards it. On success Stripe calls our return_url which lands
// at /dashboard?checkout=return&session_id=…; the dashboard polls workspace
// status to detect activation.
function CheckoutModal({ clientSecret, onClose }) {
  const options = { clientSecret };

  // Trap escape key.
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div role="dialog" aria-modal="true" style={overlay}>
      <div style={modal}>
        <button onClick={onClose} aria-label="Close" style={closeBtn}><X size={18} /></button>
        <div style={{ borderRadius: 8, overflow: 'hidden' }}>
          <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    </div>
  );
}

const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 };
const card = {
  background: 'var(--bg-1)', border: '1px solid var(--line-2)', borderRadius: 'var(--r-3, 8px)',
  padding: 20, display: 'flex', flexDirection: 'column', gap: 8, position: 'relative',
};
const cardRecommended = { borderColor: 'var(--accent, #ff5f1f)', boxShadow: '0 0 0 1px var(--accent, #ff5f1f)' };
const badge = {
  position: 'absolute', top: -10, left: 16, padding: '3px 10px', background: 'var(--accent, #ff5f1f)',
  color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 6, letterSpacing: '0.04em', textTransform: 'uppercase',
};
const planName = { fontSize: 14, fontWeight: 600, color: 'var(--fg-2)', textTransform: 'uppercase', letterSpacing: '0.04em' };
const planPrice = { fontSize: 28, fontWeight: 700, color: 'var(--fg-0)' };
const featureList = { listStyle: 'disc', paddingLeft: 18, margin: '8px 0', color: 'var(--fg-2)', fontSize: 13, lineHeight: 1.5 };
const subscribeBtn = {
  marginTop: 'auto', padding: '10px 14px', background: 'var(--bg-2)', color: 'var(--fg-1)',
  border: '1px solid var(--line-2)', borderRadius: 'var(--r-2, 6px)', fontWeight: 600, cursor: 'pointer',
  fontSize: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
};
const subscribeBtnRec = { background: 'var(--accent, #ff5f1f)', color: '#fff', border: 0 };
const errBox = {
  padding: 12, background: '#fef2f2', border: '1px solid #d33', borderRadius: 6, color: '#d33', fontSize: 13,
};
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(10,10,10,0.7)', backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16,
};
const modal = {
  position: 'relative', background: 'var(--bg-1, #fff)', borderRadius: 12, padding: 20,
  maxWidth: 560, width: '100%', maxHeight: '90vh', overflow: 'auto',
  boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
};
const closeBtn = {
  position: 'absolute', top: 8, right: 8, background: 'transparent', border: 0, cursor: 'pointer',
  color: 'var(--fg-2)', padding: 8, zIndex: 1,
};
